import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Package, MessageSquare, Send, CreditCard, Phone } from 'lucide-react';
import type { Order } from '../types';
import CompanyCard from '../components/orders/CompanyCard';

export const ORDER_STATUSES = {
  awaiting_payment: 'Aguardando Pagamento',
  pending: 'Pendente',
  preparation: 'Em preparação',
  awaitingStock: 'Aguardar Stock',
  awaitingPickup: 'Aguardar recolha',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  returned: 'Devolvido'
} as const;

export default function PublicOrder() {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const { orderId } = useParams();

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId!));
      if (!orderDoc.exists()) {
        setError('Encomenda não encontrada');
        return;
      }

      const orderData = orderDoc.data() as Order;

      // Only allow access to manual orders
      if (orderData.isPrivateSale || orderData.isOnlineSale) {
        setError('Encomenda não disponível');
        return;
      }

      // Load company data for payment info
      if (orderData.companyId) {
        const companyDoc = await getDoc(doc(db, 'companies', orderData.companyId));
        if (companyDoc.exists()) {
          setCompanyData(companyDoc.data());
        }
      }

      setOrder(orderData);
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Erro ao carregar encomenda');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!order || !newComment.trim()) return;

    setCommentLoading(true);
    try {
      const comment = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text: newComment.trim(),
        createdAt: new Date().toISOString(),
        createdBy: order.customer?.name || 'Cliente',
        isCustomer: true
      };

      await updateDoc(doc(db, 'orders', orderId!), {
        comments: arrayUnion(comment)
      });

      setNewComment('');
      loadOrder(); // Reload to get updated comments
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setCommentLoading(false);
    }
  };

  // Helper function to format dates
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      // If timestamp is a Firestore Timestamp, convert to Date
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      
      // Format date to Portuguese locale
      return date.toLocaleDateString('pt-PT', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="text-red-600">{error || 'Encomenda não encontrada'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-secondary text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <img
            src="/lognow_branco.svg"
            alt="LOGNOW"
            className="h-8"
          />
        </div>
      </div>

      {!loading && !error && order && (
        <div className="max-w-4xl mx-auto p-4 space-y-6">
          {/* Order Status */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-4">Encomenda #{order.orderNumber}</h2>
            <div className="flex items-center gap-2 text-lg">
              <Package className="w-6 h-6" />
              <span>Status: {ORDER_STATUSES[order.status as keyof typeof ORDER_STATUSES]}</span>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Criado em: {formatDate(order.createdAt)}
            </div>
          </div>

          {/* Company Card */}
          {companyData && (
            <CompanyCard company={companyData} />
          )}

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Itens da Encomenda</h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  {item.mainImage ? (
                    <img
                      src={item.mainImage}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      {item.quantity} x €{item.price.toFixed(2)}
                    </p>
                  </div>
                  <p className="font-medium">
                    €{(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span>€{order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <div>
                  <span className="text-gray-600">Portes</span>
                  <p className="text-sm text-gray-500">Entrega o mais rápido possivel</p>
                </div>
                <span>€{order.shippingCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Total</span>
                <span className="font-medium">€{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          {order.status === 'awaiting_payment' && companyData && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">Informações de Pagamento</h2>
              <div className="space-y-6">
                {companyData.apiKeys?.bankAccount?.iban && (
                  <div className="flex items-start gap-3">
                    <CreditCard className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium">Transferência Bancária</p>
                      <p className="text-gray-600 mt-1">IBAN:</p>
                      <p className="font-mono bg-gray-50 p-2 rounded mt-1">
                        {companyData.apiKeys.bankAccount.iban}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Valor a transferir: €{order.total.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}

                {companyData.apiKeys?.mbway?.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium">MBWay</p>
                      <p className="text-gray-600 mt-1">Número:</p>
                      <p className="font-mono bg-gray-50 p-2 rounded mt-1">
                        {companyData.apiKeys.mbway.phone}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Valor a pagar: €{order.total.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="text-primary" size={20} />
              Comentários
            </h2>

            <div className="space-y-4 mb-6">
              {order.comments?.map((comment) => (
                <div 
                  key={comment.id} 
                  className={`p-4 rounded-lg ${
                    comment.isCustomer ? 'bg-primary/10' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">{comment.createdBy}</span>
                    <span className="text-sm text-gray-500">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-gray-700">{comment.text}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Adicione um comentário..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <button
                onClick={handleAddComment}
                disabled={commentLoading || !newComment.trim()}
                className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
              >
                <Send size={20} />
                {commentLoading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}